import { Toaster } from "@/components/ui/toaster";
import InstallPage from "./pages/InstallPage";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AuthProvider } from "@/contexts/AuthContext";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ChartThemeProvider } from "@/hooks/useChartColors";
import { AdminGuard } from "@/components/admin/AdminGuard";
import { SkipToContent } from "@/components/SkipToContent";
import { ImpersonationBanner } from "@/components/admin/ImpersonationBanner";
import { InstallBanner } from "@/components/InstallBanner";
import { OfflineBanner } from "@/components/OfflineBanner";
import HomePage from "./pages/HomePage";
import UnifiedLoginPage from "./pages/UnifiedLoginPage";
import TriagePage from "./pages/TriagePage";
import ResultsPage from "./pages/ResultsPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import NotFound from "./pages/NotFound";
import CompleteProfilePage from "./pages/CompleteProfilePage";
import ProfileSettingsPage from "./pages/ProfileSettingsPage";
import AssessmentDetailPage from "./pages/AssessmentDetailPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import HipaaCompliancePage from "./pages/HipaaCompliancePage";
import BlogPage from "./pages/BlogPage";
import AiSymptomCheckerPost from "./pages/blog/AiSymptomCheckerPost";
import ReduceErWaitTimesPost from "./pages/blog/ReduceErWaitTimesPost";
import DigitalFrontDoorPost from "./pages/blog/DigitalFrontDoorPost";
import PreventUnnecessaryErVisitsPost from "./pages/blog/PreventUnnecessaryErVisitsPost";
import HealthcareTriageAutomationPost from "./pages/blog/HealthcareTriageAutomationPost";
import VirtualHealthcareTriagePost from "./pages/blog/VirtualHealthcareTriagePost";
import ClinicalDecisionSupportPost from "./pages/blog/ClinicalDecisionSupportPost";
import PatientIntakeSoftwarePost from "./pages/blog/PatientIntakeSoftwarePost";
import HospitalTriageAutomationPost from "./pages/blog/HospitalTriageAutomationPost";
import FounderPage from "./pages/FounderPage";
import AdminPatientsPage from "./pages/admin/AdminPatientsPage";
import AdminBillingPage from "./pages/admin/AdminBillingPage";
import AdminAgenciesPage from "./pages/admin/AdminAgenciesPage";
import AgencyRegisterPage from "./pages/admin/AgencyRegisterPage";
import AdminAuditLogsPage from "./pages/admin/AdminAuditLogsPage";
import AdminSupervisorsPage from "./pages/admin/AdminSupervisorsPage";
import AdminSettingsPage from "./pages/admin/AdminSettingsPage";
import AdminUserRolesPage from "./pages/admin/AdminUserRolesPage";
import AdminTransfersPage from "./pages/admin/AdminTransfersPage";
import AdminAgencyProfilePage from "./pages/admin/AdminAgencyProfilePage";
import AdminAnalyticsPage from "./pages/admin/AdminAnalyticsPage";
import AdminPatientDetailPage from "./pages/admin/AdminPatientDetailPage";
import AdminDiagnosticsPage from "./pages/admin/AdminDiagnosticsPage";
import DashboardRedirect from "./pages/DashboardRedirect";
// Role-specific dashboard pages
import SuperAdminDashboardPage from "./pages/super-admin/SuperAdminDashboardPage";
import AgencyDashboardPage from "./pages/agency/AgencyDashboardPage";
import SupervisorDashboardPage from "./pages/supervisor/SupervisorDashboardPage";
import PatientDashboardPage from "./pages/patient/PatientDashboardPage";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <ThemeProvider>
        <LanguageProvider>
        <ChartThemeProvider>
          <AuthProvider>
            <AdminAuthProvider>
              <TooltipProvider>
              <Toaster />
              <Sonner />
            <BrowserRouter>
            <OfflineBanner />
            <ImpersonationBanner />
            <InstallBanner />
            <div className="min-h-screen flex flex-col">
              <Routes>
                {/* Role-specific dashboard routes */}
                <Route path="/super-admin/dashboard" element={<AdminGuard requiredRole="super_admin"><SuperAdminDashboardPage /></AdminGuard>} />
                <Route path="/agency/dashboard" element={<AdminGuard><AgencyDashboardPage /></AdminGuard>} />
                <Route path="/supervisor/dashboard" element={<AdminGuard><SupervisorDashboardPage /></AdminGuard>} />
                <Route path="/patient/dashboard" element={<PatientDashboardPage />} />
                
                {/* Dashboard redirects (role-aware) */}
                <Route path="/admin/dashboard" element={<DashboardRedirect />} />
                <Route path="/dashboard" element={<DashboardRedirect />} />
                
                {/* Admin routes */}
                <Route path="/admin/login" element={<Navigate to="/login" replace />} />
                <Route path="/admin/register" element={<AgencyRegisterPage />} />
                <Route path="/admin/patients" element={<AdminGuard><AdminPatientsPage /></AdminGuard>} />
                <Route path="/admin/patients/:patientId" element={<AdminGuard><AdminPatientDetailPage /></AdminGuard>} />
                <Route path="/admin/billing" element={<AdminGuard><AdminBillingPage /></AdminGuard>} />
                <Route path="/admin/agencies" element={<AdminGuard requiredRole="super_admin"><AdminAgenciesPage /></AdminGuard>} />
                <Route path="/admin/audit-logs" element={<AdminGuard requiredRole="super_admin"><AdminAuditLogsPage /></AdminGuard>} />
                <Route path="/admin/user-roles" element={<AdminGuard><AdminUserRolesPage /></AdminGuard>} />
                <Route path="/admin/supervisors" element={<AdminGuard><AdminSupervisorsPage /></AdminGuard>} />
                <Route path="/admin/transfers" element={<AdminGuard><AdminTransfersPage /></AdminGuard>} />
                <Route path="/admin/settings" element={<AdminGuard><AdminSettingsPage /></AdminGuard>} />
                <Route path="/admin/profile" element={<AdminGuard><AdminAgencyProfilePage /></AdminGuard>} />
                <Route path="/admin/analytics" element={<AdminGuard><AdminAnalyticsPage /></AdminGuard>} />
                <Route path="/admin/diagnostics" element={<AdminGuard requiredRole="super_admin"><AdminDiagnosticsPage /></AdminGuard>} />
                
                {/* Public routes (with header/footer) */}
                <Route path="/*" element={
                  <>
                    <SkipToContent />
                    <Header />
                    <main id="main-content" className="flex-1" tabIndex={-1}>
                      <Routes>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/login" element={<UnifiedLoginPage />} />
                        <Route path="/complete-profile" element={<CompleteProfilePage />} />
                        <Route path="/profile" element={<ProfileSettingsPage />} />
                        <Route path="/triage" element={<TriagePage />} />
                        <Route path="/results" element={<ResultsPage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/founder" element={<FounderPage />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/assessment/:sessionId" element={<AssessmentDetailPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/terms" element={<TermsPage />} />
                        <Route path="/hipaa-compliance" element={<HipaaCompliancePage />} />
                        <Route path="/install" element={<InstallPage />} />
                        <Route path="/blog" element={<BlogPage />} />
                        <Route path="/blog/ai-symptom-checker-hospitals" element={<AiSymptomCheckerPost />} />
                        <Route path="/blog/reduce-er-wait-times" element={<ReduceErWaitTimesPost />} />
                        <Route path="/blog/digital-front-door-healthcare" element={<DigitalFrontDoorPost />} />
                        <Route path="/blog/prevent-unnecessary-er-visits" element={<PreventUnnecessaryErVisitsPost />} />
                        <Route path="/blog/healthcare-triage-automation" element={<HealthcareTriageAutomationPost />} />
                        <Route path="/blog/virtual-healthcare-triage-system" element={<VirtualHealthcareTriagePost />} />
                        <Route path="/blog/clinical-decision-support-tool" element={<ClinicalDecisionSupportPost />} />
                        <Route path="/blog/patient-intake-software" element={<PatientIntakeSoftwarePost />} />
                        <Route path="/blog/hospital-triage-automation" element={<HospitalTriageAutomationPost />} />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </main>
                    <Footer />
                  </>
                } />
              </Routes>
            </div>
            </BrowserRouter>
            </TooltipProvider>
          </AdminAuthProvider>
        </AuthProvider>
        </ChartThemeProvider>
        </LanguageProvider>
      </ThemeProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
